/**
 * Eager preload for large media (video) and common UI sounds.
 * Complements musicCache.preloadTrack — warms HTTP cache and decode pipelines early.
 */

import { resolveAssetPath } from './musicCache';

const videoPreloadPromises = new Map();

/**
 * Preload a background video (e.g. welcome screen). Resolves on canplay (enough to start playback).
 * @param {string} src - Absolute path from site root (e.g. /background.mp4)
 * @returns {Promise<void>}
 */
export function preloadVideo(src) {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }
  if (!src || typeof src !== 'string') {
    throw new Error('preloadVideo: src must be a non-empty string');
  }
  const resolvedSrc = resolveAssetPath(src);
  if (videoPreloadPromises.has(resolvedSrc)) {
    return videoPreloadPromises.get(resolvedSrc);
  }

  const promise = new Promise((resolve, reject) => {
    if (!document.body) {
      reject(new Error('preloadVideo: document.body not available'));
      return;
    }

    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none';

    const cleanup = () => {
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', onError);
      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }
    };

    const onCanPlay = () => {
      cleanup();
      resolve();
    };

    const onError = () => {
      cleanup();
      reject(new Error(`preloadVideo failed: ${resolvedSrc}`));
    };

    video.addEventListener('canplay', onCanPlay, { once: true });
    video.addEventListener('error', onError, { once: true });
    video.src = resolvedSrc;
    document.body.appendChild(video);
    video.load();
  });

  videoPreloadPromises.set(resolvedSrc, promise);
  return promise;
}

/**
 * Preload short sound effects (new Audio per path; triggers network fetch + decode).
 * @param {string[]} paths
 * @returns {Promise<PromiseSettledResult<unknown>[]>}
 */
export function preloadSoundEffectPaths(paths) {
  if (!Array.isArray(paths) || paths.length === 0) {
    return Promise.resolve([]);
  }
  return Promise.allSettled(
    paths.map(
      (p) =>
        new Promise((resolve, reject) => {
          if (!p) {
            reject(new Error('preloadSoundEffectPaths: empty path'));
            return;
          }
          const resolved = resolveAssetPath(p);
          const audio = new Audio(resolved);
          audio.preload = 'auto';
          if (audio.readyState >= 2) {
            resolve();
            return;
          }
          const ok = () => {
            audio.removeEventListener('canplaythrough', ok);
            audio.removeEventListener('error', bad);
            resolve();
          };
          const bad = () => {
            audio.removeEventListener('canplaythrough', ok);
            audio.removeEventListener('error', bad);
            reject(new Error(`Sound preload failed: ${resolved}`));
          };
          audio.addEventListener('canplaythrough', ok, { once: true });
          audio.addEventListener('error', bad, { once: true });
          audio.load();
        })
    )
  );
}

/** Paths used on welcome + globally; safe to warm at app boot */
export const BOOT_PRELOAD_VIDEO = '/background.mp4';
export const BOOT_PRELOAD_SOUNDS = ['/sounds/click.mp3'];

const imagePreloadPromises = new Map();
const battleBackgroundPreloadPromises = new Map();

/**
 * Preload a still image into the browser cache.
 * @param {string} src - Absolute path from site root (e.g. /unknown_backgrounds/woods_a.jpg)
 * @returns {Promise<void>}
 */
export function preloadImage(src) {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }
  if (!src || typeof src !== 'string') {
    throw new Error('preloadImage: src must be a non-empty string');
  }
  const resolvedSrc = resolveAssetPath(src);
  if (imagePreloadPromises.has(resolvedSrc)) {
    return imagePreloadPromises.get(resolvedSrc);
  }

  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
    };
    img.onload = () => {
      cleanup();
      resolve();
    };
    img.onerror = () => {
      cleanup();
      reject(new Error(`preloadImage failed: ${resolvedSrc}`));
    };
    img.src = resolvedSrc;
  });

  imagePreloadPromises.set(resolvedSrc, promise);
  return promise;
}

/**
 * Preload a battle background (still image or mp4) before BattleUI mounts.
 * @param {string} src
 * @returns {Promise<void>}
 */
export function preloadBattleBackgroundMedia(src) {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }
  if (!src || typeof src !== 'string') {
    throw new Error('preloadBattleBackgroundMedia: src must be a non-empty string');
  }
  const resolvedSrc = resolveAssetPath(src);
  if (battleBackgroundPreloadPromises.has(resolvedSrc)) {
    return battleBackgroundPreloadPromises.get(resolvedSrc);
  }

  const promise = /\.(mp4|webm)(\?|$)/i.test(resolvedSrc)
    ? preloadVideo(resolvedSrc)
    : preloadImage(resolvedSrc);

  battleBackgroundPreloadPromises.set(resolvedSrc, promise);
  return promise;
}
